import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoDivorcioResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosDivorcio = () => {
  const [patrimonioTotal, setPatrimonioTotal] = useState("");
  const [regimeBens, setRegimeBens] = useState<'comunhao_parcial' | 'comunhao_universal' | 'separacao_bens'>('comunhao_parcial');
  const [resultado, setResultado] = useState<CalculoDivorcioResult | null>(null);
  
  const { calcularDivorcio, loading } = useCalculos();

  useCalculoPrefill("divorcio", (v) => {
    setPatrimonioTotal(v.patrimonioTotal ?? "");
    setRegimeBens(v.regimeBens ?? "comunhao_parcial");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!patrimonioTotal) {
      return;
    }

    try {
      const result = await calcularDivorcio({
        patrimonioTotal: parseFloat(patrimonioTotal),
        regimeBens
      });
      setResultado(result);
      addToHistory({
        type: "divorcio",
        label: "Partilha - Divórcio",
        values: { patrimonioTotal, regimeBens },
        summary: `${regimeBens.replace(/_/g, " ")} • Patrimônio ${formatCurrency(parseFloat(patrimonioTotal))}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Partilha de Bens - Divórcio</CardTitle>
        <p className="text-sm text-muted-foreground">
          Divisão do patrimônio conforme regime de bens
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="patrimonio">Patrimônio Total</Label>
            <Input
              id="patrimonio"
              type="number"
              placeholder="500000.00"
              value={patrimonioTotal}
              onChange={(e) => setPatrimonioTotal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="regime">Regime de Bens</Label>
            <Select value={regimeBens} onValueChange={(value: any) => setRegimeBens(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comunhao_parcial">Comunhão Parcial</SelectItem>
                <SelectItem value="comunhao_universal">Comunhão Universal</SelectItem>
                <SelectItem value="separacao_bens">Separação de Bens</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Partilha"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Partilha dos Bens:</h3>
            {regimeBens === 'separacao_bens' ? (
              <div className="text-center">
                <p className="text-lg text-muted-foreground">
                  Não há partilha no regime de separação de bens
                </p>
                <p className="text-sm mt-2">
                  Cada cônjuge mantém seus bens particulares
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="text-center p-3 bg-blue-50 rounded">
                  <p><strong>Cônjuge 1</strong></p>
                  <p className="text-lg font-bold text-blue-600">
                    {formatCurrency(resultado.quinhaoConjuge1)}
                  </p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <p><strong>Cônjuge 2</strong></p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(resultado.quinhaoConjuge2)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};